import { Scroller } from '@theme/scrolling';

/**
 * Horizontal scroll progress indicator for slideshow carousels.
 * Syncs thumb size and position with the parent slideshow scroller.
 */
class SlideshowScrollProgress extends HTMLElement {
  /** @type {import('@theme/scrolling').Scroller | null} */
  #scroll = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {HTMLElement | null} */
  #thumb = null;

  connectedCallback() {
    const slideshow = this.closest('slideshow-component');
    const scroller = slideshow?.querySelector('slideshow-slides');

    this.#thumb = this.querySelector('.slideshow-scroll-progress__thumb');

    if (!(scroller instanceof HTMLElement) || !this.#thumb) {
      this.hidden = true;
      return;
    }

    this.#scroll = new Scroller(scroller, { onScroll: this.#update });
    this.#resizeObserver = new ResizeObserver(this.#update);
    this.#resizeObserver.observe(scroller);

    this.#update();
  }

  disconnectedCallback() {
    this.#scroll?.destroy();
    this.#scroll = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  #update = () => {
    const scroller = this.#scroll?.element;
    const thumb = this.#thumb;

    if (!(scroller instanceof HTMLElement) || !thumb) return;

    const { scrollWidth, clientWidth, scrollLeft } = scroller;
    const maxScroll = scrollWidth - clientWidth;

    if (maxScroll <= 1) {
      this.hidden = true;
      return;
    }

    this.hidden = false;

    const thumbWidthPercent = (clientWidth / scrollWidth) * 100;
    const thumbOffsetPercent = (scrollLeft / maxScroll) * (100 - thumbWidthPercent);

    thumb.style.width = `${thumbWidthPercent}%`;
    thumb.style.insetInlineStart = `${thumbOffsetPercent}%`;
  };
}

if (!customElements.get('slideshow-scroll-progress')) {
  customElements.define('slideshow-scroll-progress', SlideshowScrollProgress);
}
